/**
 * The v3 OpenAPI contract, read live — never hand-maintained.
 *
 * `GET {baseUrl}/openapi.json` is the source of truth (187 paths / 94 webhook events /
 * 238 schemas at the time this was written). Everything the console shows — the
 * endpoint list, the parameter/body forms, the required scopes — is derived from
 * whatever this call returns, so a route added to api.v3 tomorrow shows up here without
 * a code change.
 */

export interface OpenAPIDoc {
	openapi: string;
	info: { title: string; version: string; summary?: string; description?: string };
	servers?: { url: string; description?: string }[];
	tags?: { name: string; description?: string }[];
	'x-tagGroups'?: { name: string; tags: string[] }[];
	security?: any[];
	paths: Record<string, any>;
	components?: {
		schemas?: Record<string, any>;
		parameters?: Record<string, any>;
		responses?: Record<string, any>;
		headers?: Record<string, any>;
		securitySchemes?: Record<string, any>;
	};
	webhooks?: Record<string, any>;
}

export const trimSlash = (u: string) => u.replace(/\/+$/, '');

export async function fetchContract(baseUrl: string): Promise<OpenAPIDoc> {
	const res = await fetch(`${trimSlash(baseUrl)}/openapi.json`, {
		headers: { Accept: 'application/json' },
	});
	if (!res.ok) {
		throw new Error(
			`GET ${trimSlash(baseUrl)}/openapi.json -> ${res.status} ${res.statusText}`
		);
	}
	return res.json();
}

/**
 * Contract prose is rendered as the contract wrote it.
 *
 * THERE IS NO CLIENT-SIDE REDACTION LIST, AND THAT IS DELIBERATE. An earlier
 * version carried one — regexes naming the providers behind KYC, AML, KYB and
 * the face comparison, replaced with neutral wording before rendering. But this
 * file is bundled and served to the browser, so that list published in the page
 * source exactly the names it existed to hide, to anyone who opened the bundle.
 * A filter that names what it conceals conceals nothing.
 *
 * The contract itself is the place this is enforced: it is vendor-neutral at
 * source (verified — no provider name appears anywhere in it), and the wire
 * value for the face comparison is `simplici_face_compare`. If a vendor name
 * ever reaches the contract again, the fix belongs there, not in a redaction
 * pass here.
 */
export function sanitize<T extends string | undefined>(text: T): T {
	return text;
}

export function resolveRef(doc: OpenAPIDoc, ref: string): any {
	if (!ref || !ref.startsWith('#/')) return undefined;
	let node: any = doc;
	for (const part of ref.slice(2).split('/')) {
		if (node == null) return undefined;
		node = node[part];
	}
	return node;
}

export interface ParamDef {
	name: string;
	in: 'path' | 'query' | 'header' | 'cookie';
	required: boolean;
	schema: any;
	description?: string;
}

export interface RequestBodyDef {
	required: boolean;
	contentType: string;
	schema: any;
	/** Other content types the operation also accepts (e.g. multipart) — surfaced as a
	 *  note; the console always sends `contentType` (JSON whenever it is offered). */
	alsoAccepts: string[];
}

export interface Operation {
	id: string;
	method: string;
	path: string;
	summary: string;
	description?: string;
	tags: string[];
	scopes: string[];
	/** `security: []` on the operation (only `/oauth/token` and a couple of Meta routes) */
	anonymous: boolean;
	parameters: ParamDef[];
	requestBody?: RequestBodyDef;
	responses: Record<string, any>;
	rateClass?: string;
	events?: string[];
	backing?: string;
}

const METHODS = ['get', 'post', 'put', 'patch', 'delete'] as const;

function dedupeParams(raw: any[], doc: OpenAPIDoc): ParamDef[] {
	const byKey = new Map<string, ParamDef>();
	for (const p0 of raw || []) {
		const p = p0 && p0.$ref ? resolveRef(doc, p0.$ref) : p0;
		if (!p || !p.name || !p.in) continue;
		byKey.set(`${p.in}:${p.name}`, {
			name: p.name,
			in: p.in,
			required: !!p.required,
			schema: p.schema || {},
			description: sanitize(p.description),
		});
	}
	return Array.from(byKey.values());
}

export function listOperations(doc: OpenAPIDoc): Operation[] {
	const ops: Operation[] = [];
	for (const [path, item] of Object.entries(doc.paths || {})) {
		const pathParams = Array.isArray((item as any)?.parameters) ? (item as any).parameters : [];
		for (const method of METHODS) {
			const op = (item as any)?.[method];
			if (!op || typeof op !== 'object') continue;
			const parameters = dedupeParams([...pathParams, ...(op.parameters || [])], doc);
			const security: any[] = op.security ?? doc.security ?? [];
			const anonymous = Array.isArray(security) && security.length === 0;
			const scopes = Array.from(
				new Set(
					(Array.isArray(security) ? security : []).flatMap((s: any) => s?.oauth2 || [])
				)
			);

			let requestBody: RequestBodyDef | undefined;
			if (op.requestBody) {
				const content = op.requestBody.content || {};
				const types = Object.keys(content);
				const contentType: string = content['application/json']
					? 'application/json'
					: content['application/x-www-form-urlencoded']
						? 'application/x-www-form-urlencoded'
						: types[0] || 'application/json';
				requestBody = {
					required: !!op.requestBody.required,
					contentType,
					schema: content[contentType]?.schema,
					alsoAccepts: types.filter(t => t !== contentType),
				};
			}

			ops.push({
				id: op.operationId || `${method}_${path}`,
				method: method.toUpperCase(),
				path,
				summary: sanitize(op.summary) || op.operationId || path,
				description: sanitize(op.description),
				tags: op.tags || [],
				scopes,
				anonymous,
				parameters,
				requestBody,
				responses: op.responses || {},
				rateClass: op['x-rate-class'],
				events: op['x-events'],
				backing: op['x-backing'],
			});
		}
	}
	return ops;
}

export interface TagBucket {
	name: string;
	description?: string;
	ops: Operation[];
}

export interface TagGroup {
	name: string;
	tags: TagBucket[];
}

export function groupOperations(doc: OpenAPIDoc, ops: Operation[]): TagGroup[] {
	const byTag = new Map<string, Operation[]>();
	for (const op of ops) {
		const tags = op.tags.length ? op.tags : ['Other'];
		for (const t of tags) {
			if (!byTag.has(t)) byTag.set(t, []);
			byTag.get(t)!.push(op);
		}
	}
	for (const list of Array.from(byTag.values())) {
		list.sort((a: Operation, b: Operation) => a.path.localeCompare(b.path) || a.method.localeCompare(b.method));
	}
	const tagDescr = new Map((doc.tags || []).map(t => [t.name, sanitize(t.description)]));

	const groups: TagGroup[] = [];
	const used = new Set<string>();
	for (const g of doc['x-tagGroups'] || []) {
		const tags = g.tags
			.filter(t => byTag.has(t))
			.map(t => {
				used.add(t);
				return { name: t, description: tagDescr.get(t), ops: byTag.get(t)! };
			});
		if (tags.length) groups.push({ name: g.name, tags });
	}
	const rest = Array.from(byTag.keys()).filter(t => !used.has(t));
	if (rest.length) {
		groups.push({
			name: 'Other',
			tags: rest.map(t => ({ name: t, description: tagDescr.get(t), ops: byTag.get(t)! })),
		});
	}
	return groups;
}

export function paramKey(p: Pick<ParamDef, 'in' | 'name'>): string {
	return `${p.in}:${p.name}`;
}

export function operationMatches(op: Operation, query: string): boolean {
	if (!query) return true;
	const q = query.toLowerCase();
	return (
		op.path.toLowerCase().includes(q) ||
		op.summary.toLowerCase().includes(q) ||
		op.id.toLowerCase().includes(q) ||
		op.tags.some(t => t.toLowerCase().includes(q)) ||
		op.method.toLowerCase().includes(q)
	);
}
